// src/components/news/Pagination.tsx
"use client";
import styles from "./news.module.css";

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (p: number) => void;
};

export default function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null;

  const renderPageButton = (page: number) => (
    <button
      key={page}
      className={`${styles.pagNum} ${page === currentPage ? styles.pagNumActive : ""}`}
      onClick={() => onPageChange(page)}
    >
      {page}
    </button>
  );

  const renderPages = () => {
    if (totalPages <= 6) {
      return Array.from({ length: totalPages }, (_, i) => i + 1).map((page) =>
        renderPageButton(page)
      );
    }

    const windowSize = 3;
    let windowStart: number;

    if (currentPage <= 2) {
      windowStart = 1;
    } else if (currentPage >= totalPages - 1) {
      windowStart = Math.max(totalPages - (windowSize - 1), 1);
    } else {
      windowStart = currentPage - 1;
    }

    const windowEnd = Math.min(windowStart + windowSize - 1, totalPages);
    const lastStart = Math.max(totalPages - 2, 1);

    const sequence: Array<number | "ellipsis"> = [];
    const addedPages = new Set<number>();

    const pushNumber = (num: number) => {
      if (addedPages.has(num)) return;
      sequence.push(num);
      addedPages.add(num);
    };

    const pushEllipsis = () => {
      if (sequence[sequence.length - 1] !== "ellipsis") {
        sequence.push("ellipsis");
      }
    };

    // Luôn thêm số 1 ở đầu (trừ khi đã có trong window)
    if (windowStart > 1) {
      pushNumber(1);
      if (windowStart > 2) {
        pushEllipsis();
      }
    }

    for (let page = windowStart; page <= windowEnd; page += 1) {
      pushNumber(page);
    }

    if (windowEnd < lastStart - 1) {
      pushEllipsis();
    }

    for (let page = lastStart; page <= totalPages; page += 1) {
      pushNumber(page);
    }

    return sequence.map((item, index) =>
      item === "ellipsis" ? (
        <span key={`ellipsis-${index}`} className={styles.pagEllipsis}>
          ...
        </span>
      ) : (
        renderPageButton(item)
      )
    );
  };

  return (
    <div className={styles.pagination}>
      <button
        className={styles.pagBtn}
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
      >
        Trước
      </button>

      {renderPages()}

      <button
        className={styles.pagBtn}
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
      >
        Sau
      </button>
    </div>
  );
}
